// /src/Components/Clients/AdminTemplateView.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Building2 } from "lucide-react";
import whatsapp from "../../Assets/whatsapp_background.jpg"

import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";

export default function AdminTemplateView() {
  const { getWhatsappNumbers, getWhatsappTemplatesV1 } = useWhatsapp();

  const { clientId, templateName } = useParams();
  const decodedTemplate = decodeURIComponent(templateName || "");
  const { api } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [phoneId, setPhoneId] = useState("");
  const [templates, setTemplates] = useState([]);

  // Load WhatsApp numbers for this client
  const loadNumbers = async () => {
    if (!clientId) return;

    try {
      const result =
        await getWhatsappNumbers(clientId);

      if (result.success) {
        const list = result.data;

        setNumbers(list);

        if (list.length) {
          setPhoneId((prev) =>
            prev || list[0].phone_no_id
          );
        }
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading WhatsApp numbers");
    }
  };

  // Load templates for selected number
  const loadTemplates = async () => {
    if (!phoneId) return;

    setLoading(true);

    try {
      const result =
        await getWhatsappTemplatesV1({
          userid: clientId,
          phoneNumberId: phoneId,
        });

      if (result.success) {
        setTemplates(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Error loading templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadNumbers(); }, [clientId]);           // eslint-disable-line
  useEffect(() => { loadTemplates(); }, [phoneId]);          // eslint-disable-line

  // Pick the needed template by name (or id fallback)
  const current = useMemo(() => {
    const byName = templates.find((t) => t.name === decodedTemplate);
    if (byName) return byName;
    return templates.find((t) => t.id === decodedTemplate) || null;
  }, [templates, decodedTemplate]);

  const header = current?.components?.find((c) => c.type === "HEADER")?.text;
  const body = current?.components?.find((c) => c.type === "BODY")?.text;
  const footer = current?.components?.find((c) => c.type === "FOOTER")?.text;
  const buttons = current?.components?.find((c) => c.type === "BUTTONS")?.buttons || [];
  const mediaFormat = current?.components?.find((c) => c.format)?.format; // IMAGE / VIDEO / DOCUMENT

  const fill = (text) =>
    (text || "").replace(/{{(\d+)}}/g, (_, i) => (["John Doe", "Messaging"][i - 1] || ""));

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
          <Building2 size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Template View</h1>
          <p className="text-sm text-gray-500">
            {decodedTemplate} — {clientId}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-4 rounded-2xl border bg-white p-3">
        <div className="mb-2 text-sm font-semibold text-gray-700">Controls</div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">WhatsApp Number</div>
            <select
              className="h-10 w-80 rounded-xl border bg-white px-3 text-sm outline-none"
              value={phoneId}
              onChange={(e) => setPhoneId(e.target.value)}
            >
              <option value="">Select number</option>
              {numbers.map((n) => (
                <option key={n.phone_no_id} value={n.phone_no_id}>
                  {n.phone_no_id} — ({n.phone_no})
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Left: Meta table */}
        <div className="rounded-2xl border bg-white p-4">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading…</div>
          ) : !current ? (
            <div className="py-16 text-center text-gray-500">
              {phoneId ? "Template not found for this number." : "Select a WhatsApp number to load."}
            </div>
          ) : (
            <>
              <h3 className="mb-2 text-base font-semibold">Template Info</h3>
              <div className="overflow-hidden rounded-xl border">
                <table className="min-w-full text-sm">
                  <tbody>
                    <Row name="Template ID" value={current.id} />
                    <Row name="Template Name" value={current.name} />
                    <Row name="Language" value={current.language} />
                    <Row name="Category" value={current.category} />
                    <Row name="Status" value={current.status} />
                    <Row
                      name="Has Buttons"
                      value={buttons.length ? `${buttons.length} button(s)` : "No"}
                    />
                    <Row
                      name="Header Media"
                      value={["IMAGE", "VIDEO", "DOCUMENT"].includes(mediaFormat || "") ? mediaFormat : "—"}
                    />
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Right: Phone-style preview */}
        <div className="rounded-2xl border bg-white p-4">
          <h3 className="mb-2 text-base font-semibold">Preview</h3>
          {!current ? (
            <div className="py-16 text-center text-gray-500">—</div>
          ) : (
            <div className="flex justify-center">

              <div className="relative overflow-hidden rounded-[42px] border-[10px] border-black bg-black shadow-2xl">

                {/* notch */}
                <div className="absolute left-1/2 top-0 z-30 h-6 w-40 -translate-x-1/2 rounded-b-3xl bg-black" />

                {/* phone */}
                <div className="flex h-[480px] w-[300px] flex-col overflow-hidden bg-[#efeae2]">

                  {/* navbar */}
                  <div className="z-20 flex items-center gap-3 bg-[#075E54] px-4 pb-3 pt-8 text-white shadow-md">

                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#01C37D] text-sm font-bold text-white">
                      C
                    </div>

                    <div className="flex-1">

                      <div className="text-sm font-semibold text-white">
                        Chaafo
                      </div>

                      <div className="text-[11px] text-green-100">
                        online
                      </div>

                    </div>

                  </div>

                  {/* chat container */}
                  <div
                    className="flex-1 overflow-y-auto bg-cover bg-center p-4"
                    style={{
                      backgroundImage: `url(${whatsapp})`,
                    }}
                  >

                    <div className="flex flex-col gap-3">

                      {/* date */}
                      <div className="sticky top-0 z-10 flex justify-center">

                        <div className="rounded-xl bg-[#e1f2fb] px-3 py-1 text-[11px] text-gray-600 shadow-sm">
                          Today
                        </div>

                      </div>

                      {/* sender bubble */}
                      <div className="ml-auto max-w-[75%] rounded-2xl rounded-tr-md bg-[#DCF8C6] px-4 py-2 shadow-sm">

                        <div className="text-sm text-gray-800">
                          Hello 👋
                        </div>

                        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-gray-500">

                          <span>10:41 AM</span>

                          <svg
                            viewBox="0 0 16 15"
                            height="15"
                            width="16"
                            preserveAspectRatio="xMidYMid meet"
                            className="fill-[#53bdeb]"
                          >
                            <path d="M15.01 3.316l-8.369 8.418-4.53-4.425-1.12 1.122 5.65 5.527 9.49-9.54-1.12-1.102z"></path>
                          </svg>

                        </div>

                      </div>

                      {/* template bubble */}
                      <div className="max-w-[88%] overflow-hidden rounded-2xl rounded-tl-md bg-white shadow-sm">

                        {/* media */}
                        {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                          mediaFormat || ""
                        ) && (

                            <div className="border-b bg-gray-100 p-3">

                              <div className="flex h-44 items-center justify-center rounded-xl bg-gray-200 text-sm font-medium text-gray-500">

                                {mediaFormat} Preview

                              </div>

                            </div>
                          )}

                        {/* content */}
                        <div className="space-y-3 px-4 py-3">

                          {/* header */}
                          {header && (
                            <div className="text-[15px] font-bold text-gray-900">
                              {header}
                            </div>
                          )}

                          {/* body */}
                          {body && (
                            <div className="whitespace-pre-wrap text-[14px] leading-6 text-gray-800">
                              {fill(body)}
                            </div>
                          )}

                          {/* buttons */}
                          {!!buttons.length && (

                            <div className="border-t bg-white">

                              {buttons.map((b, i) => (

                                <button
                                  key={i}
                                  className="flex w-full items-center justify-center border-b px-4 py-3 text-sm font-medium text-[#00a884] transition hover:bg-gray-50 last:border-b-0"
                                >
                                  {b.text}
                                </button>

                              ))}

                            </div>
                          )}
                          {/* footer */}
                          {footer && (
                            <div className="border-t pt-2">

                              <div className=" flex justify-between text-[11px] text-gray-500">
                                {footer}
                                <span className="text-[10px] text-gray-400">10:42 AM</span>
                              </div>

                            </div>

                          )}

                          {/* time */}


                        </div>


                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ name, value }) {
  return (
    <tr className="border-b last:border-0">
      <th className="w-[40%] px-4 py-2 text-left text-xs font-semibold text-gray-500">{name}</th>
      <td className="px-4 py-2 text-sm">{value ?? "—"}</td>
    </tr>
  );
}
